/**
 * SectionLabel Component
 *
 * Renders the "| LABEL" pattern used throughout the Verdict design.
 * Vertical gold bar with typewriter blink effect + uppercase text.
 *
 * Design Philosophy:
 * - Editorial/legal brief feel (like a margin annotation)
 * - Distinct from Factory.ai's dot pattern
 * - Typewriter cursor blink adds life and engagement
 * - Signals "Document/Dossier" not "Server Status"
 *
 * Usage: <SectionLabel>RESEARCH</SectionLabel>
 * Renders: | RESEARCH (with blinking bar)
 */

import { cn } from '@/lib/utils';

interface SectionLabelProps {
  children: string;
  className?: string;
  /** Use lines on both sides: — LABEL — */
  centered?: boolean;
  /** Disable the typewriter blink animation */
  static?: boolean;
}

export function SectionLabel({
  children,
  className,
  centered = false,
  static: isStatic = false,
}: SectionLabelProps) {
  if (centered) {
    // Centered variant with horizontal lines: — LABEL —
    return (
      <div className={cn('flex items-center justify-center gap-3', className)}>
        <div
          className={cn(
            'h-px w-8 bg-verdict-primary/50',
            !isStatic && 'animate-pulse'
          )}
        />
        <span className="verdict-label text-verdict-primary">{children}</span>
        <div
          className={cn(
            'h-px w-8 bg-verdict-primary/50',
            !isStatic && 'animate-pulse'
          )}
        />
      </div>
    );
  }

  // Default variant with trailing underscore: LABEL___
  return (
    <div className={cn('flex items-center', className)}>
      {/* Label text - Editorial brutalism: tiny with wide tracking */}
      <span className="verdict-label text-verdict-primary">{children}</span>
      {/* Trailing underscore with slow blink - longer for visual emphasis */}
      {!isStatic && (
        <span className="verdict-label text-verdict-primary animate-pulse-slow">_</span>
      )}
    </div>
  );
}

